import axios from 'axios';
import { getToken, clearAuth } from '../utils/auth';

const API_URL = process.env.REACT_APP_API_URL || 'https://dev.chatutil.top';

// 配置 axios
const api = axios.create({
  baseURL: API_URL,
});

// 请求拦截器：自动添加 token 和设置 Content-Type
api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  
  // 如果没有手动设置 Content-Type 且不是 FormData，设置为 application/json
  if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  
  return config;
});

// 响应拦截器：处理 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('🚨 API 请求错误:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data
    });
    
    if (error.response?.status === 401) {
      console.error('🔐 检测到 401 错误，清除 token 并跳转登录');
      clearAuth();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Template 接口定义（新格式）
export interface Template {
  id: string;
  title: string;
  prompt: string;
  category: string;
  previewImage: string;
  sourceImage?: string;
  targetAudience?: string[];
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  isActive: boolean;
  sortOrder: number;
}

export interface TemplatesResponse {
  success: boolean;
  data: {
    templates: Template[];
    pagination: {
      page: number;
      pageSize: number;
      total: number;
      hasMore: boolean;
    };
    metadata: {
      lastModified: string;
      version: string;
    };
  };
}

export interface CreateTemplateRequest {
  title: string;
  prompt: string;
  category: string;
  previewImage: string;
  sourceImage?: string;
  targetAudience?: string[];
  tags?: string[];
  isActive?: boolean;
  sortOrder?: number;
}

export interface UpdateTemplateRequest {
  title?: string;
  prompt?: string;
  category?: string;
  previewImage?: string;
  sourceImage?: string;
  targetAudience?: string[];
  tags?: string[];
  isActive?: boolean;
  sortOrder?: number;
}

// API 函数

export const listTemplates = async (params?: {
  category?: string;
  page?: number;
  page_size?: number;
  since?: string;
}): Promise<TemplatesResponse> => {
  const response = await api.get<TemplatesResponse>('/templates', { params });
  return response.data;
};

export const getTemplate = async (id: string): Promise<Template> => {
  const response = await api.get<{ success: boolean; data: Template }>(`/templates/${id}`);
  return response.data.data;
};

export const createTemplate = async (data: CreateTemplateRequest): Promise<Template> => {
  const response = await api.post<{ success: boolean; data: Template }>(
    '/templates',
    data
  );
  // 后端现在返回完整的 Template 对象
  return response.data.data;
};

export const updateTemplate = async (id: string, data: UpdateTemplateRequest): Promise<void> => {
  await api.put(`/templates/${id}`, data);
};

export const deleteTemplate = async (id: string): Promise<void> => {
  await api.delete(`/templates/${id}`);
};

/**
 * 批量删除模板
 */
export const batchDeleteTemplates = async (ids: string[]): Promise<{
  success: boolean;
  message: string;
  data: { deleted_count: number };
}> => {
  const response = await api.post('/templates/batch-delete', { ids });
  return response.data;
};

// 上传图片
export const uploadImage = async (file: File): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  // 完全不传 headers 配置：
  // - 让 axios 拦截器自动添加 Authorization header
  // - 让浏览器自动设置 Content-Type (multipart/form-data; boundary=...)
  const response = await api.post<{ success: boolean; data: { url: string } }>(
    '/upload/image',
    formData
  );

  return response.data.data.url;
};
